/**
 * Policy edit form component with cloud provider tabs
 */
import { useState } from 'react';

import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Paper,
  Tab,
  Tabs,
  TextField,
  makeStyles,
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import Autocomplete from '@material-ui/lab/Autocomplete';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(3),
    position: 'relative',
  },
  tabPanel: {
    paddingTop: theme.spacing(2),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  buttonGroup: {
    marginTop: theme.spacing(3),
    display: 'flex',
    gap: theme.spacing(1),
  },
  notice: {
    marginBottom: theme.spacing(2),
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    borderRadius: theme.shape.borderRadius,
  },
  loadingContent: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
  },
}));

// Common AWS regions
const AWS_REGIONS = [
  'us-east-1',
  'us-east-2',
  'us-west-1',
  'us-west-2',
  'eu-west-1',
  'eu-west-2',
  'eu-central-1',
  'ap-southeast-1',
  'ap-southeast-2',
  'ap-northeast-1',
];

// Common Azure regions
const AZURE_REGIONS = [
  'eastus',
  'eastus2',
  'westus',
  'westus2',
  'northeurope',
  'westeurope',
  'southeastasia',
  'japaneast',
];

// Common GCP regions
const GCP_REGIONS = [
  'us-central1',
  'us-east1',
  'us-west1',
  'europe-west1',
  'europe-west2',
  'asia-southeast1',
  'asia-northeast1',
];

interface PolicyPermissions {
  aws?: {
    accounts: string[];
    regions: string[];
    allowed_iam_roles?: string[];
    partitions?: string[];
  };
  azure?: {
    subscriptions: string[];
    tenants: string[];
    regions: string[];
  };
  gcp?: {
    project_ids: string[];
    regions: string[];
  };
}

interface PolicyEditFormProps {
  initialPermissions: PolicyPermissions;
  onSave: (permissions: PolicyPermissions) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
  saving?: boolean;
}

/**
 * Validate AWS account ID format
 */
export function validateAwsAccountId(accountId: string): string | null {
  if (!/^\d{12}$/.test(accountId)) {
    return 'AWS account ID must be exactly 12 digits';
  }
  return null;
}

/**
 * Validate AWS region format
 */
export function validateAwsRegion(region: string): string | null {
  if (!/^[a-z]{2}-[a-z]+-\d+$/.test(region)) {
    return 'Invalid region format (e.g., us-east-1)';
  }
  return null;
}

/**
 * Validate Azure subscription GUID
 */
export function validateAzureSubscription(subId: string): string | null {
  if (
    !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
      subId,
    )
  ) {
    return 'Azure subscription must be a valid GUID';
  }
  return null;
}

/**
 * Validate GCP project ID
 */
export function validateGcpProjectId(projectId: string): string | null {
  if (!/^[a-z][a-z0-9-]{4,28}[a-z0-9]$/.test(projectId)) {
    return 'GCP project ID must be 6-30 lowercase letters, digits, or hyphens';
  }
  return null;
}

export const PolicyEditForm = ({
  initialPermissions,
  onSave,
  onCancel,
  loading = false,
  saving = false,
}: PolicyEditFormProps) => {
  const classes = useStyles();
  const [activeTab, setActiveTab] = useState(0);
  const [permissions, setPermissions] = useState<PolicyPermissions>(initialPermissions);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleTabChange = (_event: React.ChangeEvent<{}>, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleAwsAccountsChange = (
    _event: React.ChangeEvent<{}>,
    value: (string | string[])[],
  ) => {
    const stringValues = Array.isArray(value) ? value.flat().filter((v): v is string => typeof v === 'string') : [];
    // Validate accounts
    const newErrors: { [key: string]: string } = { ...errors };
    delete newErrors.awsAccounts;

    const invalidAccounts = stringValues.filter(
      acc => validateAwsAccountId(acc) !== null,
    );
    if (invalidAccounts.length > 0) {
      newErrors.awsAccounts = `Invalid account IDs: ${invalidAccounts.join(', ')}`;
    }

    setErrors(newErrors);
    setPermissions({
      ...permissions,
      aws: { 
        ...(permissions.aws || undefined), 
        accounts: stringValues, 
        regions: permissions.aws?.regions || [] 
      },
    });
  };

  const handleAwsRegionsChange = (
    _event: React.ChangeEvent<{}>,
    value: (string | string[])[],
  ) => {
    const stringValues = Array.isArray(value) ? value.flat().filter((v): v is string => typeof v === 'string') : [];
    // Validate regions
    const newErrors: { [key: string]: string } = { ...errors };
    delete newErrors.awsRegions;

    const invalidRegions = stringValues.filter(reg => validateAwsRegion(reg) !== null);
    if (invalidRegions.length > 0) {
      newErrors.awsRegions = `Invalid regions: ${invalidRegions.join(', ')}`;
    }

    setErrors(newErrors);
    setPermissions({
      ...permissions,
      aws: { 
        ...(permissions.aws || undefined), 
        regions: stringValues, 
        accounts: permissions.aws?.accounts || [] 
      },
    });
  };

  const handleAzureSubscriptionsChange = (
    _event: React.ChangeEvent<{}>,
    value: (string | string[])[],
  ) => {
    const stringValues = Array.isArray(value) ? value.flat().filter((v): v is string => typeof v === 'string') : [];
    // Validate subscriptions
    const newErrors: { [key: string]: string } = { ...errors };
    delete newErrors.azureSubscriptions;

    const invalidSubs = stringValues.filter(
      sub => validateAzureSubscription(sub) !== null,
    );
    if (invalidSubs.length > 0) {
      newErrors.azureSubscriptions = `Invalid subscription GUIDs: ${invalidSubs.join(', ')}`;
    }

    setErrors(newErrors);
    setPermissions({
      ...permissions,
      azure: { ...(permissions.azure || { tenants: [], regions: [] }), subscriptions: stringValues },
    });
  };

  const handleAzureRegionsChange = (
    _event: React.ChangeEvent<{}>,
    value: (string | string[])[],
  ) => {
    const stringValues = Array.isArray(value) ? value.flat().filter((v): v is string => typeof v === 'string') : [];
    setPermissions({
      ...permissions,
      azure: { ...(permissions.azure || { tenants: [], subscriptions: [] }), regions: stringValues },
    });
  };

  const handleGcpProjectIdsChange = (
    _event: React.ChangeEvent<{}>,
    value: (string | string[])[],
  ) => {
    const stringValues = Array.isArray(value) ? value.flat().filter((v): v is string => typeof v === 'string') : [];
    // Validate project IDs
    const newErrors: { [key: string]: string } = { ...errors };
    delete newErrors.gcpProjectIds;

    const invalidProjects = stringValues.filter(
      proj => validateGcpProjectId(proj) !== null,
    );
    if (invalidProjects.length > 0) {
      newErrors.gcpProjectIds = `Invalid project IDs: ${invalidProjects.join(', ')}`;
    }

    setErrors(newErrors);
    setPermissions({
      ...permissions,
      gcp: { 
        ...(permissions.gcp || undefined), 
        project_ids: stringValues, 
        regions: permissions.gcp?.regions || [] 
      },
    });
  };

  const handleGcpRegionsChange = (
    _event: React.ChangeEvent<{}>,
    value: (string | string[])[],
  ) => {
    const stringValues = Array.isArray(value) ? value.flat().filter((v): v is string => typeof v === 'string') : [];
    setPermissions({
      ...permissions,
      gcp: { 
        ...(permissions.gcp || undefined), 
        regions: stringValues, 
        project_ids: permissions.gcp?.project_ids || [] 
      },
    });
  };

  const handleSave = async () => {
    // No field presence validation - any combination is valid
    // Format validation already happens in the onChange handlers
    // Backend will handle business rules (e.g., required IAM roles)

    // Check if there are any format errors from onChange handlers
    if (Object.keys(errors).length > 0) {
      return;
    }

    await onSave(permissions);
  };

  const hasErrors = Object.keys(errors).length > 0;

  return (
    <Paper className={classes.root}>
      {saving && (
        <Box className={classes.loadingOverlay}>
          <Box className={classes.loadingContent}>
            <CircularProgress size={60} />
            <Box>Saving policy changes...</Box>
          </Box>
        </Box>
      )}
      <Alert severity="info" className={classes.notice}>
        Edit the authorized cloud accounts and regions for this repository. Changes will be
        reflected immediately after saving.
      </Alert>

      <Tabs value={activeTab} onChange={handleTabChange}>
        <Tab label="AWS" />
        <Tab label="Azure" />
        <Tab label="GCP" />
      </Tabs>

      {/* AWS Tab */}
      {activeTab === 0 && (
        <Box className={classes.tabPanel}>
          <FormControl fullWidth className={classes.formField}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={permissions.aws?.accounts || []}
              onChange={handleAwsAccountsChange}
              onBlur={e => {
                const input = e.target as HTMLInputElement;
                const raw = input.value.trim();
                if (raw) {
                  const current = permissions.aws?.accounts || [];
                  handleAwsAccountsChange({} as React.ChangeEvent<{}>, [...current, raw]);
                  input.value = '';
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { key, ...tagProps } = getTagProps({ index }) as { key: string };
                  return <Chip key={key} label={option} {...tagProps} />;
                })
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="AWS Account IDs"
                  placeholder="Enter 12-digit account ID and press Enter"
                  error={!!errors.awsAccounts}
                  helperText={
                    errors.awsAccounts || 'Enter AWS account IDs (12 digits each)'
                  }
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth className={classes.formField}>
            <Autocomplete
              multiple
              freeSolo
              options={AWS_REGIONS}
              value={permissions.aws?.regions || []}
              onChange={handleAwsRegionsChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { key, ...tagProps } = getTagProps({ index }) as { key: string };
                  return <Chip key={key} label={option} {...tagProps} />;
                })
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="AWS Regions"
                  placeholder="Select or enter regions"
                  error={!!errors.awsRegions}
                  helperText={
                    errors.awsRegions || 'Select regions or enter custom ones'
                  }
                />
              )}
            />
          </FormControl>
        </Box>
      )}

      {/* Azure Tab */}
      {activeTab === 1 && (
        <Box className={classes.tabPanel}>
          <FormControl fullWidth className={classes.formField}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={permissions.azure?.subscriptions || []}
              onChange={handleAzureSubscriptionsChange}
              onBlur={e => {
                const input = e.target as HTMLInputElement;
                const raw = input.value.trim();
                if (raw) {
                  const current = permissions.azure?.subscriptions || [];
                  handleAzureSubscriptionsChange({} as React.ChangeEvent<{}>, [...current, raw]);
                  input.value = '';
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { key, ...tagProps } = getTagProps({ index }) as { key: string };
                  return <Chip key={key} label={option} {...tagProps} />;
                })
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Azure Subscription IDs"
                  placeholder="Enter subscription GUID and press Enter"
                  error={!!errors.azureSubscriptions}
                  helperText={
                    errors.azureSubscriptions ||
                    'Enter Azure subscription GUIDs'
                  }
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth className={classes.formField}>
            <Autocomplete
              multiple
              freeSolo
              options={AZURE_REGIONS}
              value={permissions.azure?.regions || []}
              onChange={handleAzureRegionsChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { key, ...tagProps } = getTagProps({ index }) as { key: string };
                  return <Chip key={key} label={option} {...tagProps} />;
                })
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="Azure Regions"
                  placeholder="Select or enter regions"
                  error={!!errors.azureRegions}
                  helperText={
                    errors.azureRegions || 'Select regions or enter custom ones'
                  }
                />
              )}
            />
          </FormControl>
        </Box>
      )}

      {/* GCP Tab */}
      {activeTab === 2 && (
        <Box className={classes.tabPanel}>
          <FormControl fullWidth className={classes.formField}>
            <Autocomplete
              multiple
              freeSolo
              options={[]}
              value={permissions.gcp?.project_ids || []}
              onChange={handleGcpProjectIdsChange}
              onBlur={e => {
                const input = e.target as HTMLInputElement;
                const raw = input.value.trim();
                if (raw) {
                  const current = permissions.gcp?.project_ids || [];
                  handleGcpProjectIdsChange({} as React.ChangeEvent<{}>, [...current, raw]);
                  input.value = '';
                }
              }}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { key, ...tagProps } = getTagProps({ index }) as { key: string };
                  return <Chip key={key} label={option} {...tagProps} />;
                })
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="GCP Project IDs"
                  placeholder="Enter project ID and press Enter"
                  error={!!errors.gcpProjectIds}
                  helperText={
                    errors.gcpProjectIds ||
                    'Enter GCP project IDs (6-30 lowercase chars)'
                  }
                />
              )}
            />
          </FormControl>

          <FormControl fullWidth className={classes.formField}>
            <Autocomplete
              multiple
              freeSolo
              options={GCP_REGIONS}
              value={permissions.gcp?.regions || []}
              onChange={handleGcpRegionsChange}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => {
                  // eslint-disable-next-line @typescript-eslint/no-unused-vars
                  const { key, ...tagProps } = getTagProps({ index }) as { key: string };
                  return <Chip key={key} label={option} {...tagProps} />;
                })
              }
              renderInput={params => (
                <TextField
                  {...params}
                  label="GCP Regions"
                  placeholder="Select or enter regions"
                  error={!!errors.gcpRegions}
                  helperText={
                    errors.gcpRegions || 'Select regions or enter custom ones'
                  }
                />
              )}
            />
          </FormControl>
        </Box>
      )}

      <Box className={classes.buttonGroup}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={loading || saving || hasErrors}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
        <Button variant="outlined" onClick={onCancel} disabled={loading || saving}>
          Cancel
        </Button>
      </Box>
    </Paper>
  );
};
